import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faPenClip } from "@fortawesome/pro-solid-svg-icons";
import { faStickyNote } from "@fortawesome/pro-solid-svg-icons/faStickyNote";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popconfirm from "antd/es/popconfirm";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import { Alert, Button, TextAreaWithMentions } from "@mapmycustomers/ui";

import useFormSchema from "@app/component/FormFields/utils/useFormSchema";
import ActivityNotePreview from "@app/component/preview/components/ActivityNotePreview";
import { getMe } from "@app/store/iam";
import { getUsers, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import getActivityAllowedUsers from "@app/util/activity/getActivityAllowedUsers";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { activityLayoutModel } from "@app/util/layout/impl";

import styles from "./ActivityNote.module.scss";
import messages from "./messages";

const noFilter = () => true;

interface Props {
  activity: Activity;
  allUsers: User[];
  className?: string;
  loading?: boolean;
  me: Iam;
  onSave?: (data: { activity: Activity; onSuccess: () => void; updated?: boolean }) => void;
  users: User[];
}

const ActivityNote: React.FC<Props> = ({
  activity,
  allUsers,
  className,
  loading,
  me,
  onSave,
  users,
}) => {
  const intl = useIntl();
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [exitPopoverOpen, setExitPopoverOpen] = useState<boolean>(false);
  const [note, setNote] = useState<null | string>(activity.note);
  const [editing, startEditing, stopEditing] = useBoolean();
  const [modified, setAsModified, unsetAsModified] = useBoolean();

  const layout = activityLayoutModel.getLayoutFor(activity);
  const [actualSchema] = useFormSchema(EntityType.ACTIVITY, layout, noFilter);
  const isRequired = actualSchema.find(({ field }) => field === ActivityFieldName.NOTE)?.required;

  const notesField = activityFieldModel.getByName(ActivityFieldName.NOTE);
  const disableNoteEditing = !notesField?.isEditable;

  const disableDelete = isRequired || !notesField?.isEditable;

  const allowedEmails: User["username"][] = useMemo(
    () =>
      getActivityAllowedUsers(
        me.id,
        users,
        activity.userIds ?? [],
        activity.visibility,
        activity.teamId ?? [],
        activity?.assignee
      ),
    [activity, me, users]
  );

  const handleIsMentionDisabled = useCallback(
    (user: User) => {
      return !allowedEmails.includes(user.username);
    },
    [allowedEmails]
  );

  const allMentionedUsersHaveAccessToActivity = useMemo(() => {
    const allEmails = new Set(users.map(({ username }) => username));
    return (
      (note ?? "")
        .split(" ")
        .filter(
          (value) =>
            value[0] === "@" &&
            allEmails.has(value.slice(1)) &&
            !allowedEmails.includes(value.slice(1))
        ).length === 0
    );
  }, [allowedEmails, note, users]);

  const resetNote = useCallback(() => {
    if (activity.note) {
      stopEditing();
    }
    unsetAsModified();
    setNote(activity.note);
  }, [activity.note, setNote, stopEditing, unsetAsModified]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen((popoverOpen) => {
        if (open && !activity.note) {
          startEditing();
        } else if (!open) {
          if (editing && modified) {
            setExitPopoverOpen(true);
            return popoverOpen;
          } else {
            setExitPopoverOpen(false);
            resetNote();
          }
        }
        return open;
      });
    },
    [activity.note, editing, modified, resetNote, startEditing]
  );

  const handleExitPopoverOpenChange = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange]
  );

  const handleCancelExit = useCallback(() => setExitPopoverOpen(false), [setExitPopoverOpen]);

  const handleCancelEdit = useCallback(() => {
    if (activity.note) {
      stopEditing();
    } else {
      setExitPopoverOpen(false);
      setPopoverOpen(false);
      resetNote();
    }
  }, [activity.note, resetNote, setExitPopoverOpen, setPopoverOpen, stopEditing]);

  const handleHidePopoverConfirm = useCallback(() => {
    setExitPopoverOpen(false);
    setPopoverOpen(false);
    resetNote();
  }, [resetNote, setExitPopoverOpen, setPopoverOpen]);

  const handleModify = useCallback(
    (empty: boolean) => {
      if (!empty) {
        setAsModified();
      }
    },
    [setAsModified]
  );

  const handleHidePopover = useCallback(
    (isUpdate: boolean) => {
      if (isUpdate) {
        stopEditing();
      } else {
        startEditing();
        setNote(null);
      }
      unsetAsModified();
      setPopoverOpen(false);
    },
    [setNote, setPopoverOpen, startEditing, stopEditing, unsetAsModified]
  );

  const handleSave = useCallback(() => {
    onSave?.({
      activity: { ...activity, note },
      onSuccess: () => handleHidePopover(true),
      updated: !!activity?.note,
    });
  }, [activity, handleHidePopover, note, onSave]);

  const handleDelete = useCallback(() => {
    onSave?.({
      activity: { ...activity, note: null },
      onSuccess: () => handleHidePopover(false),
      updated: !!activity?.note,
    });
  }, [activity, handleHidePopover, onSave]);

  const disabled = editing && (!modified || isEmptyString(note ?? ""));

  useEffect(() => {
    setNote(activity.note);
  }, [activity.note]);

  return (
    <Popover
      content={
        <div className={styles.noteContainer}>
          {editing ? (
            <>
              <div className={styles.header}>
                <span className={styles.title}>{intl.formatMessage(messages.note)}</span>
                <Popconfirm
                  cancelText={intl.formatMessage(messages.no)}
                  okText={intl.formatMessage(messages.yes)}
                  onCancel={handleCancelExit}
                  onConfirm={handleHidePopoverConfirm}
                  onOpenChange={handleExitPopoverOpenChange}
                  open={exitPopoverOpen}
                  placement="right"
                  title={intl.formatMessage(messages.leaveConfirm)}
                >
                  <Button size="small" type="control">
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </Popconfirm>
              </div>
              <TextAreaWithMentions
                allUsers={allUsers}
                autoFocus
                className={styles.note}
                hideDisabledUsers
                isMentionDisabled={handleIsMentionDisabled}
                maxLength={Number.MAX_SAFE_INTEGER}
                onChange={setNote}
                onModify={handleModify}
                placeholder={intl.formatMessage(messages.placeholder)}
                rows={4}
                value={note ?? ""}
              />
            </>
          ) : (
            <ActivityNotePreview
              activity={activity}
              className={styles.paragraph}
              currentUserId={me.id}
              note={activity.note ?? ""}
              users={users}
            />
          )}

          {!allMentionedUsersHaveAccessToActivity && (
            <Alert
              className={styles.notAllMentionedUsersHaveAccessToActivity}
              message={intl.formatMessage(messages.alert)}
              showIcon
              type="warning"
            />
          )}
          <div className={styles.btnContainer}>
            {editing ? (
              <>
                <Button onClick={handleCancelEdit} size="small">
                  {intl.formatMessage(messages.cancel)}
                </Button>
                <Button
                  disabled={disabled}
                  loading={loading}
                  onClick={handleSave}
                  size="small"
                  type="primary"
                >
                  {intl.formatMessage(messages.save)}
                </Button>
              </>
            ) : disableNoteEditing ? null : (
              <>
                <Popconfirm
                  cancelText={intl.formatMessage(messages.cancel)}
                  disabled={disableDelete}
                  okText={intl.formatMessage(messages.yes)}
                  onConfirm={handleDelete}
                  placement="bottom"
                  title={intl.formatMessage(messages.deleteConfirm)}
                >
                  <Button danger disabled={disableDelete} iconOnly type="text">
                    <Tooltip placement="top" title={intl.formatMessage(messages.delete)}>
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </Tooltip>
                  </Button>
                </Popconfirm>
                <Button disabled={disableNoteEditing} iconOnly onClick={startEditing} type="text">
                  <Tooltip placement="top" title={intl.formatMessage(messages.edit)}>
                    <FontAwesomeIcon icon={faPenClip} size="sm" />
                  </Tooltip>
                </Button>
              </>
            )}
          </div>
        </div>
      }
      destroyTooltipOnHide
      onOpenChange={handleOpenChange}
      open={popoverOpen}
      placement="bottomLeft"
      trigger={!activity.note || editing ? ["click"] : ["hover"]}
    >
      {!activity.note && disableNoteEditing ? (
        <></>
      ) : (
        <div
          className={cn(styles.container, activity.note && styles.hasNote, className)}
          onClick={stopEvents}
        >
          <FontAwesomeIcon className={styles.icon} icon={faStickyNote} />
          <span className={styles.label}>
            {activity.note
              ? intl.formatMessage(messages.viewNote)
              : intl.formatMessage(messages.addNote)}
          </span>
        </div>
      )}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsersOfEntireOrganization(state),
  me: getMe(state)!,
  users: getUsers(state),
});

export default connect(mapStateToProps)(ActivityNote);
